if (!customElements.get('ds24-quantity-offer')) {
  customElements.define('ds24-quantity-offer', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.config = JSON.parse(this.querySelector('[data-offer-config]').textContent);
      this.input = this.querySelector('input[name="quantity"]');
      this.variant = this.config.variants.find(v => String(v.id) === this.dataset.variant);
      this.cart = this.config.cart;
      this.form = document.getElementById(this.dataset.form);
      this.cards = [...this.querySelectorAll('[data-offer-quantity]')];
      const signal = this.controller.signal;
      this.cards.forEach(button => button.addEventListener('click', () => this.choose(Number(button.dataset.offerQuantity)), {signal}));
      this.querySelector('[data-offer-minus]').addEventListener('click', () => this.choose(Number(this.input.value) - this.variant.step), {signal});
      this.querySelector('[data-offer-plus]').addEventListener('click', () => this.choose(Number(this.input.value) + this.variant.step), {signal});
      this.input.addEventListener('input', () => { if (this.input.value !== '' && this.input.validity.valid) this.refresh(); }, {signal});
      this.input.addEventListener('change', () => this.choose(Number(this.input.value)), {signal});
      this.form?.addEventListener('variant:change', event => {
        const variant = this.config.variants.find(v => v.id === event.detail.variant.id);
        if (variant) { this.variant = variant; this.refresh(); }
      }, {signal});
      if (window.theme?.pubsub) {
        this.variantUnsubscribe = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, event => {
          if (event.data.sectionId !== this.dataset.section || String(event.data.productId) !== this.dataset.product) return;
          const source = event.data.parsedHTML.getElementById(this.id);
          if (source) {
            const fresh = JSON.parse(source.querySelector('[data-offer-config]').textContent);
            this.config.variants = fresh.variants;
            this.cart = fresh.cart;
          }
          this.variant = this.config.variants.find(v => v.id === event.data.variant.id);
          this.refresh();
        });
        this.cartUnsubscribe = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => this.readCart());
      }
      this.expiryTimer = setInterval(() => this.refresh(), 30000);
      const untilExpiry = Number(this.dataset.end) * 1000 - Date.now();
      if (untilExpiry > 0 && untilExpiry < 2147483647) this.expiryDeadline = setTimeout(() => this.refresh(), untilExpiry);
      this.refresh();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.cartController?.abort();
      this.variantUnsubscribe?.();
      this.cartUnsubscribe?.();
      clearInterval(this.expiryTimer);
      clearTimeout(this.expiryDeadline);
    }
    async readCart() {
      this.cartController?.abort();
      this.cartController = new AbortController();
      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {signal:this.cartController.signal});
        if (!response.ok) return;
        this.cart = (await response.json()).items;
        this.refresh();
      } catch (error) {
        if (error.name !== 'AbortError') this.refresh();
      }
    }
    countCart(variantOnly = false) {
      return this.cart.filter(item => variantOnly ? item.variant_id === this.variant.id : String(item.product_id) === this.dataset.product).reduce((sum,item) => sum + item.quantity,0);
    }
    maxQuantity() {
      let max = this.variant.max ?? 9999;
      if (this.variant.managed && this.variant.policy === 'deny') max = Math.min(max,this.variant.stock);
      return Math.max(0,max - this.countCart(true));
    }
    choose(quantity) {
      const max = this.maxQuantity();
      const min = Math.min(this.variant.min,max);
      const step = this.variant.step || 1;
      const normalized = min + Math.floor((quantity - min) / step) * step;
      this.input.value = Math.max(min,Math.min(max,Number.isFinite(normalized) ? normalized : min));
      this.input.dispatchEvent(new Event('input',{bubbles:true}));
      this.refresh();
    }
    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE',{style:'currency',currency:this.config.currency || 'EUR'}).format(cents / 100);
    }
    active() {
      const now = Date.now() / 1000;
      return now >= Number(this.dataset.start) && now < Number(this.dataset.end) && this.dataset.b2b !== 'true';
    }
    total(quantity) {
      const discounted = this.active() && this.countCart() + quantity >= 3;
      const discount = discounted ? Math.floor((this.variant.price * Number(this.dataset.percent)) / 100) : 0;
      return (this.variant.price - discount) * quantity;
    }
    refresh() {
      if (!this.variant) return;
      const max = this.maxQuantity();
      const b2b = this.dataset.b2b === 'true';
      const offer = this.active() && max + this.countCart() >= 3;
      const labels = this.config.labels;
      const quantity = Math.max(0,Math.min(max,Number(this.input.value) || this.variant.min));
      this.input.value = quantity;
      this.input.min = Math.min(this.variant.min,max);
      this.input.max = max;
      this.input.step = this.variant.step;
      this.input.disabled = !this.variant.available || max === 0;
      this.querySelector('[data-offer-minus]').disabled = this.input.disabled || quantity <= Number(this.input.min);
      this.querySelector('[data-offer-plus]').disabled = this.input.disabled || quantity >= max;
      this.querySelector('[data-offer-title]').textContent = offer ? labels.heading : labels.choose;
      this.querySelector('[data-offer-badge]').hidden = !offer;
      this.cards.forEach(button => {
        const count = Number(button.dataset.offerQuantity);
        const validStep = (count - this.variant.min) % this.variant.step === 0;
        button.disabled = count > max || count < this.variant.min || !validStep || !this.variant.available;
        button.setAttribute('aria-pressed',String(count === quantity));
        const price = button.querySelector('[data-offer-price]');
        price.hidden = b2b || button.disabled;
        price.textContent = this.money(this.total(count));
        const compare = button.querySelector('[data-offer-compare]');
        compare.hidden = button.disabled || b2b || this.total(count) >= this.variant.price * count;
        compare.querySelector('s').textContent = this.money(this.variant.price * count);
        button.querySelector('[data-choice-note]').textContent = button.disabled ? labels.unavailable : (!b2b && this.total(count) < this.variant.price * count ? labels.save : labels.total);
      });
      const unit = quantity === 1 ? this.config.unit : this.config.units;
      const saving = this.variant.price * quantity - this.total(quantity);
      this.querySelector('[data-offer-summary]').textContent = b2b ? '' : labels.selection + ': ' + quantity + ' ' + unit + ' · ' + this.money(this.total(quantity)) + (saving > 0 ? ' · ' + labels.saving + ' ' + this.money(saving) : '');
      this.querySelector('[data-offer-note]').textContent = b2b ? labels.b2b : offer ? labels.auto : this.active() ? labels.stock : labels.regular;
      this.querySelector('[data-offer-terms]').hidden = !offer;
      const productInfo = this.closest('product-info');
      if (!this.kochDisclaimer && labels.koch) {
        this.kochDisclaimer = [...(productInfo?.querySelectorAll('.product__text p') || [])].find(p => p.textContent.replace(/\s+/g,' ').trim() === '*Artikel vom Hersteller Koch Chemie sind von der Rabattierung ausgeschlossen.');
        if (this.kochDisclaimer) this.kochOriginal = this.kochDisclaimer.innerHTML;
      }
      if (this.kochDisclaimer) {
        if (this.active()) this.kochDisclaimer.textContent = labels.koch;
        else this.kochDisclaimer.innerHTML = this.kochOriginal;
      }
      const shippingGoal = productInfo?.querySelector('.ds24-product-shipping-goal');
      if (shippingGoal && labels.shipping) {
        const message = document.createElement('span');
        message.className = 'text-sm leading-tight';
        message.textContent = labels.shipping;
        shippingGoal.replaceChildren(message);
      }
    }
  });
}
